import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listPlatformDominios } from "../../../api/client";
import { useAuthContext } from "../../../context/AuthContext";
import { useTeamSocketPresence } from "../../../hooks/useTeamSocketPresence";
import {
  MAX_PLATFORM_PROBE_SOCKETS,
  normalizeWorkspaceHost,
  useWorkspaceProbeSockets,
} from "../../../hooks/useWorkspaceProbeSockets";
import { getSocketWorkspace } from "../../../utils/socketWorkspace";

export function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pushPresenceRows(rows, workspaceHost, tabsByUserId, namesByUserId, presenceDetailByUserId) {
  if (!workspaceHost) return;
  const det = presenceDetailByUserId || new Map();
  for (const [userId, tabs] of tabsByUserId) {
    if (tabs <= 0) continue;
    rows.push({
      key: `${workspaceHost}::${userId}`,
      workspace_host: workspaceHost,
      user_id: userId,
      nome: namesByUserId.get(userId) || `ID ${userId}`,
      tabs,
      presence_detail: det.get(userId) ?? null,
    });
  }
}

export function usePlatformConsoleOnlinePresence({ onLoaded, onOuterError } = {}) {
  const { t } = useTranslation("app");
  const { bearerToken, userData } = useAuthContext();
  const {
    tabsByUserId,
    namesByUserId,
    presenceDetailByUserId,
    socketConnected: mainSocketConnected,
  } = useTeamSocketPresence(userData);

  const [loadingDominios, setLoadingDominios] = useState(true);
  const [dominiosError, setDominiosError] = useState("");
  const [allWorkspaceHosts, setAllWorkspaceHosts] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const currentWorkspaceNorm = normalizeWorkspaceHost(getSocketWorkspace());

  const workspaceHostList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const h of allWorkspaceHosts) {
      const n = normalizeWorkspaceHost(h);
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    out.sort();
    return out;
  }, [allWorkspaceHosts]);

  const probeWorkspaceHosts = useMemo(() => {
    if (!currentWorkspaceNorm) return workspaceHostList;
    return workspaceHostList.filter((h) => h !== currentWorkspaceNorm);
  }, [workspaceHostList, currentWorkspaceNorm]);

  const truncatedTotal = workspaceHostList.length > MAX_PLATFORM_PROBE_SOCKETS;

  const loadDominios = useCallback(async () => {
    setLoadingDominios(true);
    setDominiosError("");
    try {
      const res = await listPlatformDominios({
        limit: 220,
        tenantLegacyStats: "0",
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      const hosts = items
        .map((row) => row?.dominio)
        .filter((d) => {
          const n = normalizeWorkspaceHost(d);
          return Boolean(n);
        });
      setAllWorkspaceHosts(hosts);
      onLoaded?.();
    } catch (err) {
      setDominiosError(err?.message || t("platformPage.onlineLoadError"));
      setAllWorkspaceHosts([]);
      onOuterError?.();
    } finally {
      setLoadingDominios(false);
    }
  }, [t, onLoaded, onOuterError]);

  useEffect(() => {
    loadDominios();
  }, [loadDominios, refreshTick]);

  const { presenceByWorkspace, socketStats, hostsProbed } = useWorkspaceProbeSockets(probeWorkspaceHosts, {
    bearerToken,
    userId: userData?.id,
    enabled: Boolean(bearerToken && userData?.id && !loadingDominios && !dominiosError && probeWorkspaceHosts.length),
  });

  const onlineItems = useMemo(() => {
    const rows = [];
    if (mainSocketConnected && currentWorkspaceNorm) {
      pushPresenceRows(rows, currentWorkspaceNorm, tabsByUserId, namesByUserId, presenceDetailByUserId);
    }
    for (const [ws, payload] of presenceByWorkspace) {
      if (normalizeWorkspaceHost(ws) === currentWorkspaceNorm) continue;
      const tabs = payload.tabs;
      const names = payload.names;
      const detail = payload.detail || new Map();
      pushPresenceRows(rows, ws, tabs, names, detail);
    }
    rows.sort((a, b) => {
      const w = String(a.workspace_host).localeCompare(String(b.workspace_host), undefined, {
        sensitivity: "base",
      });
      if (w !== 0) return w;
      return String(a.nome).localeCompare(String(b.nome), undefined, { sensitivity: "base" });
    });
    return rows;
  }, [mainSocketConnected, currentWorkspaceNorm, tabsByUserId, namesByUserId, presenceDetailByUserId, presenceByWorkspace]);

  const probesAllConnected =
    probeWorkspaceHosts.length === 0 ||
    (socketStats.total > 0 && socketStats.connected === socketStats.total);

  const workspacesOnline = useMemo(() => {
    const byHost = new Map();
    for (const row of onlineItems) {
      const host = String(row.workspace_host || "");
      if (!byHost.has(host)) byHost.set(host, []);
      byHost.get(host).push(row);
    }
    const list = [...byHost.entries()].map(([workspace_host, users]) => ({
      workspace_host,
      users,
      count: users.length,
    }));
    list.sort((a, b) => {
      const c = b.count - a.count;
      if (c !== 0) return c;
      return String(a.workspace_host).localeCompare(String(b.workspace_host), undefined, { sensitivity: "base" });
    });
    return list;
  }, [onlineItems]);

  const refreshList = useCallback(() => {
    setRefreshTick((x) => x + 1);
  }, []);

  return {
    loadingDominios,
    dominiosError,
    workspaceHostList,
    truncatedTotal,
    mainSocketConnected,
    socketStats,
    hostsProbed,
    probesAllConnected,
    probeWorkspaceHosts,
    onlineItems,
    workspacesOnline,
    refreshList,
  };
}
